import {
  Experiment,
  GrowthBook,
  Result,
  WidenPrimitives,
} from '@growthbook/growthbook';
import { JSONValue } from '@growthbook/growthbook';
import { apiFeatureFlag } from '../api/apiFeatureFlag';
import { v4 as uuidv4 } from 'uuid';
import { mixpanelUtils } from '../integrations/mixpanel';
import { logger } from '../logger/logger';
import {
  LOGGER_FEATURE,
  LOGGER_ACTION,
} from '../../constants/logger/logger-tags';
import { STORAGE_KEYS } from '../../constants/storage/storage';
import { localStorageUtils } from '../storage/localStorage';

export interface IFeatureFlagUserAttributes {
  loggedIn: boolean;
  privacyOptedIn: boolean;
  installedTime: number;
}

class FeatureFlag {
  private readonly growthBook: GrowthBook;
  private featureFlagUserIdKey = 'featureFlagId';
  constructor() {
    this.growthBook = new GrowthBook({
      enableDevMode: true,
      trackingCallback: (
        experiment: Experiment<any>,
        result: Result<any>
      ): void => {
        mixpanelUtils.fireExperimentStart(
          experiment.key,
          result.value,
          result.variationId
        );
      },
    });
  }

  private get userId(): string {
    if (localStorage.getItem(this.featureFlagUserIdKey) === null) {
      localStorage.setItem(this.featureFlagUserIdKey, uuidv4());
    }
    return localStorage.getItem(this.featureFlagUserIdKey) || '';
  }

  public async init(userAttributes: IFeatureFlagUserAttributes): Promise<void> {
    try {
      const results = await apiFeatureFlag.loadFeatures();
      if (results) {
        await this.growthBook.setPayload({ features: results.features });
      }

      const lastKnownInstalledTime = localStorageUtils.getNumberItem(
        STORAGE_KEYS.LAST_KNOWN_INSTALLED_TIME,
        0
      );
      const currentInstalledTime = userAttributes.installedTime;
      const isReturningUser =
        lastKnownInstalledTime > 0 &&
        currentInstalledTime > 0 &&
        lastKnownInstalledTime !== currentInstalledTime;

      await this.growthBook.setAttributes({
        ...this.growthBook.getAttributes(),
        id: this.userId,
        url: window.location.href,
        country: results?.country,
        loggedIn: userAttributes.loggedIn,
        privacyOptedIn: userAttributes.privacyOptedIn,
        isReturningUser,
      });

      logger.addBreadcrumb(
        'Feature flags initialized',
        'feature-flags',
        'info',
        {
          country: results?.country,
          loggedIn: userAttributes.loggedIn,
          privacyOptedIn: userAttributes.privacyOptedIn,
          isReturningUser,
        }
      );
    } catch (error) {
      // Log as warning - feature flags are not critical, app can continue with defaults
      logger.warn('Failed to initialize feature flags, using defaults', {
        tags: {
          feature: LOGGER_FEATURE.FEATURE_FLAGS,
          action: LOGGER_ACTION.INIT,
        },
        extra: {
          errorMessage: error instanceof Error ? error.message : String(error),
        },
      });
      // Don't throw - allow app to continue with default values
    }
  }

  public getFeatureValue<T extends JSONValue>(
    key: string,
    fallbackValue: T
  ): WidenPrimitives<T> {
    return this.growthBook.getFeatureValue(key, fallbackValue);
  }

  // Merge, don't replace — consent changes mid-session and must re-resolve the variant.
  public async updateAttributes(
    partial: Record<string, unknown>
  ): Promise<void> {
    await this.growthBook.setAttributes({
      ...this.growthBook.getAttributes(),
      ...partial,
    });
  }

  public getCurrentCountry(): string {
    return (this.growthBook.getAttributes()?.country as string) ?? '';
  }
}

export const featureFlag = new FeatureFlag();
